import { folderLook } from '@idc-forms/client-app-domain/documents/folder-look'
import { useVirtualizer } from '@tanstack/react-virtual'
import { FolderOpen, SearchX } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { CapText } from '@/features/client-app-preview/components/cap-text'
import { Permission } from '@/features/roles/consts/permissions'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import { Skeleton } from '@/shared/ui/skeleton'
import {
  DOCUMENT_STATE_FILTER,
  type DocumentStateFilter,
  groupDocuments
} from '../domain/group-documents'
import { groupInheritedByOrigin, inheritsFromAnyLevel } from '../domain/inherited-documents'
import { createQueue, markItem, type UploadQueueItem } from '../domain/upload-queue'
import {
  useDocumentCategoriesQuery,
  useInheritedDocumentsQuery,
  useOpenDocumentMutation,
  useOwnerDocumentsQuery,
  useRemoveDocumentMutation,
  useRenameDocumentMutation,
  useSetDocumentCategoryMutation,
  useSetDocumentVisibilityMutation,
  useUploadDocumentsMutation
} from '../hooks/use-owner-documents'
import {
  CATALOG_OWNER_LABEL,
  CATALOG_PUBLISH_REACH,
  type CatalogOwnerType
} from '../models/document-owner'
import type { OwnedDocumentRow } from '../services/documents-admin-v2.service'
import { DocumentRenameDialog } from './document-rename-dialog'
import { DocumentToolbar } from './document-toolbar'
import { DocumentUploadDialog } from './document-upload-dialog'
import { DocumentViewerDialog } from './document-viewer-dialog'
import { PortfolioCanvas } from './portfolio-canvas'
import { type PortfolioCardDocument, PortfolioDocumentCard } from './portfolio-document-card'
import { PortfolioSection } from './portfolio-section'

/**
 * Cuántos archivos admite una tanda.
 *
 * No es un límite del backend —cada subida es su propia llamada— sino del diálogo: treinta
 * archivos son treinta requests secuenciales, y ahora que la cola dice en cuál va, treinta es un
 * rato mirable. Más que eso conviene partirlo.
 */
const PORTFOLIO_UPLOAD_BATCH = 30

/**
 * A partir de cuántas tarjetas se virtualiza una sección.
 *
 * El volumen medido hoy son unidades o decenas —el máximo real es 22, y por eso el endpoint no
 * pagina—, así que en la práctica esto casi nunca se enciende, y está bien que así sea: por
 * debajo del umbral la sección fluye con el panel y no hay un segundo contenedor de scroll que
 * atrapar con la rueda. Por encima, cada tarjeta monta una imagen y su consulta de firma, y
 * cuarenta de esas sí se notan.
 */
const VIRTUALIZE_ABOVE = 40

/** El alto estimado de una fila de la grilla: tarjeta cuadrada + dos renglones de texto. */
const ESTIMATED_ROW_HEIGHT = 196

type DocumentPortfolioTabProps = {
  ownerType: CatalogOwnerType
  ownerId: string
}

/**
 * EL CARTAPACIO DE UN DUEÑO DE CATÁLOGO — un solo componente montado cuatro veces, en sociedad,
 * proyecto, subtipo e inmueble, dentro de un panel lateral de 320 a 600 px.
 *
 * ── POR QUÉ SE DIBUJA CON LA UI DE LA APP ────────────────────────────────────
 *
 * Porque ya encajaba. La app del cliente resuelve exactamente esto —carpetas, tarjetas con
 * miniatura, la pila de hojas que distingue un documento de varias páginas de uno de una sola, el
 * visor— y lo resuelve para el MISMO archivo. Lo que faltaba no era otro dibujo: eran los
 * controles. Así que el cartapacio es la vista de la app **más una capa de edición**, y el curador
 * publica mirando lo que el cliente va a ver en lugar de mirando un nombre de archivo.
 *
 * Antes de esto la pantalla se DESBORDABA: cada fila llevaba su propio `Select` de categoría de
 * 168 px dentro de un bloque de acciones de ~407 px que no podía encogerse, contra 296 px útiles.
 * El panel entero se iba en scroll horizontal. La categoría dejó de ser un control por fila y pasó
 * a ser la SECCIÓN —que es lo que el ADR 0022 ya decía que una categoría es— y las acciones
 * secundarias se fueron al menú «⋯». Lo vigila `document-row-width.spec.ts`.
 *
 * ── LAS TRES COSAS QUE LA PANTALLA TIENE QUE DEJAR OBVIAS ────────────────────
 *
 * 1. **Subir y publicar son dos actos.** Todo nace interno; el chip de cada tarjeta lo dice
 *    siempre, en las doce, en la misma esquina.
 * 2. **Lo PROPIO y lo HEREDADO son dos conjuntos.** Se ven juntos —porque el asesor necesita ver
 *    lo que su cliente ve— pero en secciones distintas, y lo heredado no tiene una sola acción.
 *    Esa ausencia es la escalera del ADR 0023 dibujada: editar el origen se hace en el origen.
 * 3. **Publicar puede estar deshabilitado aunque subir no lo esté**: son permisos distintos, y
 *    hoy PLANIFICACION puede subir pero no publicar.
 *
 * Se llama Cartapacio y no Documentos a propósito: el proyecto YA tiene una pantalla llamada
 * «Documentos» —el editor de plantillas de contrato— y dos cosas con el mismo nombre en la misma
 * entidad es una trampa esperando.
 */
export function DocumentPortfolioTab({ ownerType, ownerId }: DocumentPortfolioTabProps) {
  const { has } = usePermissions()
  const canManage = has(Permission.ManageCatalogDocuments)
  const canPublish = has(Permission.PublishCatalogDocuments)

  const [uploadOpen, setUploadOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState<DocumentStateFilter>(DOCUMENT_STATE_FILTER.all)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [inheritedOpen, setInheritedOpen] = useState(false)
  const [queue, setQueue] = useState<UploadQueueItem[]>([])
  const [toPublish, setToPublish] = useState<OwnedDocumentRow | null>(null)
  const [toDelete, setToDelete] = useState<OwnedDocumentRow | null>(null)
  const [toRename, setToRename] = useState<{ id: string; name: string } | null>(null)
  const [viewing, setViewing] = useState<{
    id: string
    name: string
    mimeType: string | null
  } | null>(null)

  // Leer pide el MISMO permiso que escribir: una lista que no se puede tocar sigue siendo una
  // filtración de qué documentos internos existen. Sin el permiso no se dispara la consulta, así
  // no se cosecha un 403 previsible.
  const query = useOwnerDocumentsQuery({ ownerType, ownerId, enabled: canManage })
  // Una sociedad no hereda de nadie: no se pregunta lo que ya se sabe.
  const inheritedQuery = useInheritedDocumentsQuery({
    ownerType,
    ownerId,
    enabled: canManage && inheritsFromAnyLevel(ownerType)
  })
  // La plantilla es global y cambia casi nunca, así que se pide una vez y las cuatro pestañas
  // comparten la caché.
  const categoriesQuery = useDocumentCategoriesQuery(canManage)
  const uploadMutation = useUploadDocumentsMutation(ownerType, ownerId, {
    onItemChange: (key, patch) => {
      setQueue((current) => markItem(current, key, patch as Partial<Omit<UploadQueueItem, 'key'>>))
    }
  })
  const categoryMutation = useSetDocumentCategoryMutation(ownerType, ownerId)
  const visibilityMutation = useSetDocumentVisibilityMutation(ownerType, ownerId)
  const removeMutation = useRemoveDocumentMutation(ownerType, ownerId)
  const renameMutation = useRenameDocumentMutation(ownerType, ownerId)
  const openMutation = useOpenDocumentMutation()

  /**
   * El cartapacio son los PAPELES, y las fotos de la galería no lo son.
   *
   * Salían acá porque las dos pestañas comparten una sola consulta —que sigue siendo lo correcto:
   * es un conjunto, no dos— pero compartir la consulta no es compartir la VISTA. Se queda fuera
   * por SLOT y no por mime: lo que la excluye es el papel que cumple, no que sea una imagen. Un
   * plano en PNG que nadie mandó al carrusel es del cartapacio y tiene que seguir viéndose acá.
   */
  const documents = useMemo(
    () => (query.data ?? []).filter((document) => document.slot !== 'gallery'),
    [query.data]
  )

  const categories = categoriesQuery.data ?? []

  const groups = useMemo(
    () => groupDocuments({ documents, categories, search, state: stateFilter }),
    [documents, categories, search, stateFilter]
  )

  const inheritedGroups = useMemo(
    () => groupInheritedByOrigin(inheritedQuery.data ?? []),
    [inheritedQuery.data]
  )

  const pendingId =
    (visibilityMutation.isPending ? visibilityMutation.variables?.id : undefined) ??
    (categoryMutation.isPending ? categoryMutation.variables?.id : undefined) ??
    (removeMutation.isPending ? removeMutation.variables : undefined)

  if (!canManage) {
    return (
      <div className="p-3">
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FolderOpen />
            </EmptyMedia>
            <EmptyTitle>Sin acceso al cartapacio</EmptyTitle>
            <EmptyDescription>
              Necesitas el permiso <strong>ManageCatalogDocuments</strong> para ver y organizar los
              documentos de {CATALOG_OWNER_LABEL[ownerType]}.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    )
  }

  const filtering = search.trim().length > 0 || stateFilter !== DOCUMENT_STATE_FILTER.all
  const inheritedCount = inheritedQuery.data?.length ?? 0

  const openDocument = (document: { id: string; name: string; mimeType: string | null }) => {
    setViewing(document)
    openMutation.mutate(document.id)
  }

  const download = (id: string) => {
    openMutation.mutate(id, {
      onSuccess: (result) => {
        // `window.open` y no un `<a download>` sintético: el servidor ya decidió el
        // `Content-Disposition` desde el MIME —un PDF se abre, una planilla se ofrece para
        // guardar— así que lo único que hace falta del lado del navegador es abrir la URL.
        window.open(result.url, '_blank', 'noopener,noreferrer')
      }
    })
  }

  return (
    <div className="flex flex-col">
      <DocumentToolbar
        search={search}
        onSearchChange={setSearch}
        state={stateFilter}
        onStateChange={setStateFilter}
        onUpload={() => {
          setQueue([])
          setUploadOpen(true)
        }}
      />

      <PortfolioCanvas className="px-3 pt-2 pb-4">
        <CapText as="p" variant="caption" tone="muted" className="block pb-2">
          Lo que subas queda interno hasta que lo publiques. Al publicarlo lo verán{' '}
          {CATALOG_PUBLISH_REACH[ownerType]}.
        </CapText>

        {query.isPending ? (
          <div className="grid grid-cols-2 gap-4 @[480px]:grid-cols-3">
            <Skeleton className="aspect-square rounded-lg" />
            <Skeleton className="aspect-square rounded-lg" />
            <Skeleton className="aspect-square rounded-lg" />
            <Skeleton className="aspect-square rounded-lg" />
          </div>
        ) : null}

        {query.isError ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyTitle>No se pudo cargar el cartapacio</EmptyTitle>
              <EmptyDescription>Vuelve a intentarlo en un momento.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}

        {/* El vacío del FILTRO y el vacío del DUEÑO son dos cosas distintas y se dicen distinto:
            confundirlos hace que alguien concluya que no hay nada subido cuando lo que hay es un
            buscador con texto. */}
        {query.isSuccess && groups.length === 0 && filtering ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <SearchX />
              </EmptyMedia>
              <EmptyTitle>Nada coincide con la búsqueda</EmptyTitle>
              <EmptyDescription>
                Prueba con otro nombre o quita el filtro de estado.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}

        {query.isSuccess && documents.length === 0 && !filtering ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FolderOpen />
              </EmptyMedia>
              <EmptyTitle>Nada subido todavía</EmptyTitle>
              <EmptyDescription>
                Arrastra aquí planos, contratos o reglamentos, o usa «Subir». Quedan internos hasta
                que los publiques.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : null}

        {groups.map((group) => (
          <PortfolioSection
            key={group.id}
            name={group.name}
            count={group.documents.length}
            // Abiertas por defecto: el curador viene a ver qué hay, no a abrir cinco secciones.
            // Lo que se pliega es lo que ya se revisó.
            open={!collapsed[group.id]}
            onOpenChange={(open) => {
              setCollapsed((current) => ({ ...current, [group.id]: !open }))
            }}
          >
            <PortfolioGrid
              documents={group.documents.map(toCardDocument)}
              look={folderLook(group.name)}
              canPublish={canPublish}
              categories={categories}
              pendingId={pendingId}
              onOpen={openDocument}
              onToggleVisibility={(document, next) => {
                // Despublicar es inmediato: quitar algo de la vista del cliente nunca es el acto
                // peligroso. Publicar pide confirmación porque sí lo es.
                if (next === 'internal') {
                  visibilityMutation.mutate({ id: document.id, visibility: 'internal' })
                  return
                }
                const row = documents.find((item) => item.id === document.id)
                if (row) setToPublish(row)
              }}
              onRename={(document) => {
                setToRename({ id: document.id, name: document.name })
              }}
              onChangeCategory={(document, categoryId) => {
                categoryMutation.mutate({ id: document.id, categoryId })
              }}
              onDownload={(document) => {
                download(document.id)
              }}
              onDelete={(document) => {
                const row = documents.find((item) => item.id === document.id)
                if (row) setToDelete(row)
              }}
            />
          </PortfolioSection>
        ))}

        {/*
         * ── LO HEREDADO ───────────────────────────────────────────────────────
         *
         * Separado por una línea y plegado por defecto: es contexto, no el trabajo. Lo que
         * responde es «¿qué más ve mi cliente?», que se consulta de vez en cuando; dejarlo
         * abierto empujaría lo propio —que es lo que se viene a curar— fuera de la pantalla.
         */}
        {inheritedCount > 0 ? (
          <div className="mt-2 border-t border-border pt-2">
            <PortfolioSection
              name="Heredados"
              count={inheritedCount}
              muted
              open={inheritedOpen}
              onOpenChange={setInheritedOpen}
            >
              <CapText as="p" variant="caption" tone="muted" className="block pb-3">
                Publicados en un nivel de arriba. El cliente de {CATALOG_OWNER_LABEL[ownerType]} los
                ve, y se editan donde viven.
              </CapText>

              {inheritedGroups.map((group) => (
                <div key={group.originType} className="pb-4">
                  <CapText as="h3" variant="caption" tone="muted" className="block pb-2">
                    {group.label}
                  </CapText>
                  <PortfolioGrid
                    documents={group.documents.map((document) => ({
                      id: document.id,
                      name: document.name,
                      mimeType: document.mimeType,
                      pages: document.pages,
                      hasThumbnail: document.hasThumbnail,
                      thumbnailAspect: document.thumbnailAspect,
                      // Lo heredado que llega acá es, por construcción, lo PÚBLICO: el resolver
                      // filtra por audiencia de cliente. La tarjeta igual no lo dibuja — con
                      // `originType` puesto muestra el origen y esconde el chip de estado.
                      visibility: 'public' as const,
                      categoryId: document.categoryId
                    }))}
                    originType={group.originType}
                    onOpen={openDocument}
                  />
                </div>
              ))}
            </PortfolioSection>
          </div>
        ) : null}
      </PortfolioCanvas>

      <DocumentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        ownerType={ownerType}
        pending={uploadMutation.isPending}
        maxFiles={PORTFOLIO_UPLOAD_BATCH}
        canPublish={canPublish}
        publishReach={CATALOG_PUBLISH_REACH[ownerType]}
        categories={categories}
        queue={queue}
        onSubmit={(values) => {
          // La cola se arma ANTES de arrancar: así las filas ya están en pantalla cuando la
          // primera empieza a viajar, en vez de aparecer de a una.
          setQueue(createQueue(values.files))
          uploadMutation.mutate(values, {
            onSuccess: () => {
              setUploadOpen(false)
              setQueue([])
            }
          })
        }}
      />

      <DocumentRenameDialog
        document={toRename}
        pending={renameMutation.isPending}
        onOpenChange={() => {
          setToRename(null)
        }}
        onSubmit={(name) => {
          if (!toRename) return
          renameMutation.mutate(
            { id: toRename.id, name },
            {
              onSuccess: () => {
                setToRename(null)
              }
            }
          )
        }}
      />

      <DocumentViewerDialog
        document={viewing}
        onOpenChange={() => {
          setViewing(null)
          openMutation.reset()
        }}
        url={openMutation.data?.url ?? null}
        fileName={openMutation.data?.fileName}
        size={openMutation.data?.size}
        isPending={openMutation.isPending}
        isError={openMutation.isError}
      />

      <AlertDialog
        open={Boolean(toPublish)}
        onOpenChange={(open) => {
          if (!open) setToPublish(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publicar documento</AlertDialogTitle>
            {/* `break-words`: los nombres reales son de asesores escribiendo a mano y llegan a
                los 200 caracteres sin un espacio. Sin esto el diálogo se ensancha fuera de la
                pantalla y el botón de confirmar queda inalcanzable. */}
            <AlertDialogDescription className="break-words">
              “{toPublish?.name}” pasará a verse en la app de{' '}
              <strong>{CATALOG_PUBLISH_REACH[ownerType]}</strong>. Puedes despublicarlo después,
              pero quienes ya lo abrieron pudieron descargarlo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!toPublish) return
                visibilityMutation.mutate({ id: toPublish.id, visibility: 'public' })
                setToPublish(null)
              }}
            >
              Publicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(toDelete)}
        onOpenChange={(open) => {
          if (!open) setToDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar documento</AlertDialogTitle>
            <AlertDialogDescription className="break-words">
              {/* El aviso cambia según el estado porque las consecuencias son otras: un interno
                  sólo lo vio el equipo; uno publicado ya está en la app. */}
              {toDelete?.visibility === 'public' ? (
                <>
                  “{toDelete?.name}” está <strong>publicado</strong>: se eliminará también de la app
                  de {CATALOG_PUBLISH_REACH[ownerType]}. El archivo se borra y no se puede
                  recuperar.
                </>
              ) : (
                <>
                  “{toDelete?.name}” se eliminará de {CATALOG_OWNER_LABEL[ownerType]}. El archivo se
                  borra y no se puede recuperar.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!toDelete) return
                removeMutation.mutate(toDelete.id)
                setToDelete(null)
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Del DTO del listado a lo que la tarjeta dibuja. */
function toCardDocument(row: OwnedDocumentRow): PortfolioCardDocument {
  return {
    id: row.id,
    name: row.name,
    mimeType: row.mimeType,
    pages: row.pages,
    hasThumbnail: row.hasThumbnail,
    thumbnailAspect: row.thumbnailAspect,
    visibility: row.visibility,
    categoryId: row.categoryId
  }
}

type PortfolioGridProps = {
  documents: PortfolioCardDocument[]
  look?: Parameters<typeof PortfolioDocumentCard>[0]['look']
  originType?: CatalogOwnerType
  canPublish?: boolean
  categories?: Array<{ id: string; name: string }>
  pendingId?: string
  onOpen: (document: PortfolioCardDocument) => void
  onToggleVisibility?: (document: PortfolioCardDocument, next: 'public' | 'internal') => void
  onRename?: (document: PortfolioCardDocument) => void
  onChangeCategory?: (document: PortfolioCardDocument, categoryId: string | null) => void
  onDownload?: (document: PortfolioCardDocument) => void
  onDelete?: (document: PortfolioCardDocument) => void
}

/**
 * LA GRILLA DE TARJETAS de una sección, virtualizada sólo cuando hace falta.
 *
 * ## Dos columnas, tres a partir de 480 px de PANEL
 *
 * `@[480px]` y no una media query: el panel se arrastra entre 320 y 600 px **sin que el viewport
 * cambie**, así que una media query no ve absolutamente nada — la grilla se quedaría en dos
 * columnas a 600 px y en dos a 320. La consulta de contenedor mira el único ancho que se mueve.
 *
 * ## La virtualización se enciende sola y casi nunca
 *
 * Por debajo del umbral la sección fluye con el panel: un segundo contenedor de scroll anidado es
 * una trampa para la rueda del mouse y no se paga si no hace falta. Por encima, cada tarjeta monta
 * una imagen y su consulta de firma, y ahí sí conviene montar sólo lo visible.
 *
 * `useVirtualizer` directo y no `data-table-virtual-body`: ése está atado a una `Table` de
 * TanStack —`TableBody`, `TableRow`, `flexRender`— y acá lo que se virtualiza son FILAS DE UNA
 * GRILLA, no filas de tabla. Lo reusable de verdad es el hook, y es el que se reusa.
 */
function PortfolioGrid({
  documents,
  look,
  originType,
  canPublish,
  categories,
  pendingId,
  onOpen,
  onToggleVisibility,
  onRename,
  onChangeCategory,
  onDownload,
  onDelete
}: PortfolioGridProps) {
  const virtualize = documents.length > VIRTUALIZE_ABOVE

  const card = (document: PortfolioCardDocument) => (
    <PortfolioDocumentCard
      key={document.id}
      document={document}
      look={look}
      originType={originType}
      canPublish={canPublish}
      categories={categories}
      pending={pendingId === document.id}
      onOpen={() => {
        onOpen(document)
      }}
      onToggleVisibility={
        onToggleVisibility ? (next) => onToggleVisibility(document, next) : undefined
      }
      onRename={onRename ? () => onRename(document) : undefined}
      onChangeCategory={
        onChangeCategory ? (categoryId) => onChangeCategory(document, categoryId) : undefined
      }
      onDownload={onDownload ? () => onDownload(document) : undefined}
      onDelete={onDelete ? () => onDelete(document) : undefined}
    />
  )

  if (!virtualize) {
    return <div className="grid grid-cols-2 gap-4 @[480px]:grid-cols-3">{documents.map(card)}</div>
  }

  return <VirtualPortfolioGrid documents={documents} renderCard={card} />
}

/**
 * A partir de qué ancho del PANEL entran tres columnas. Es el mismo número que el `@[480px]` de
 * la grilla que no virtualiza, y está declarado una sola vez porque los dos caminos tienen que
 * cortar en el mismo lugar: si el CSS pasara a tres y la aritmética siguiera en dos, cada fila
 * virtual dibujaría dos tarjetas en una grilla de tres y quedaría un hueco por fila.
 */
const THREE_COLUMN_WIDTH = 480

/**
 * La grilla virtualizada. Vive aparte porque `useVirtualizer` es un hook y no puede colgar de una
 * rama condicional del componente de arriba: montarlo sólo «cuando hay muchos» sería cambiar la
 * cantidad de hooks entre renders, que es exactamente lo que React prohíbe.
 *
 * ## Acá las columnas se MIDEN, y no es una inconsistencia con el `@container` de al lado
 *
 * Virtualizar es repartir N tarjetas en filas, y para eso hay que saber **cuántas entran por
 * fila** — un número que el CSS conoce y JavaScript no. Dejar que la consulta de contenedor
 * decidiera el ancho mientras la aritmética asume otro número es el bug clásico de las grillas
 * virtuales: filas a medias y huecos al final del scroll. Midiendo una vez y usando ese número
 * para las dos cosas, no pueden discrepar.
 */
function VirtualPortfolioGrid({
  documents,
  renderCard
}: {
  documents: PortfolioCardDocument[]
  renderCard: (document: PortfolioCardDocument) => React.ReactNode
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [columns, setColumns] = useState(2)

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => {
      setColumns(entry.contentRect.width >= THREE_COLUMN_WIDTH ? 3 : 2)
    })
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [])

  const rows = Math.ceil(documents.length / columns)

  const virtualizer = useVirtualizer({
    count: rows,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 3
  })

  return (
    <div
      ref={scrollRef}
      // `overscroll-contain`: al llegar al final de esta lista la rueda NO sigue scrolleando el
      // panel de atrás. Sin eso, curar una sección larga arrastra la pestaña entera sin querer.
      className={cn('max-h-[60vh] overflow-y-auto overscroll-contain')}
    >
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {virtualizer.getVirtualItems().map((row) => (
          <div
            key={row.key}
            className="grid gap-4 pb-4"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${row.start}px)`
            }}
          >
            {documents.slice(row.index * columns, row.index * columns + columns).map(renderCard)}
          </div>
        ))}
      </div>
    </div>
  )
}
